// src/Components/Chat/ChatWindow.jsx
import React, { useEffect, useRef, useState } from "react";
import Message from "./ChatMessage";
import { chatService } from "../../Services/chatServices";

const ChatWindow = ({ 
  roomData, 
  currentPhase, 
  currentUserId,
  className = "" 
}) => {
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [isUserScrolled, setIsUserScrolled] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  // Get messages for current phase
  const messages = chatService.getPhaseMessages(roomData, currentPhase);

  // Create phase description message (appears as first message)
  const getPhaseDescriptionMessage = (phase) => {
    const descriptions = {
      lobby: {
        content: "Chat with other players while waiting for the game to start.",
        type: "system",
        id: `phase-desc-${phase}`,
        timestamp: new Date(Date.now() - 1000000), // Older timestamp to appear first
        playerName: "NameIt!",
        userId: "system"
      },
      review: {
        content: "Share your thoughts on the answers from this round!",
        type: "system", 
        id: `phase-desc-${phase}`,
        timestamp: new Date(Date.now() - 1000000),
        playerName: "NameIt!",
        userId: "system"
      },
      results: {
        content: "Chat about the results of the round! ",
        type: "system",
        id: `phase-desc-${phase}`,
        timestamp: new Date(Date.now() - 1000000), 
        playerName: "NameIt!",
        userId: "system"
      }
    };
    return descriptions[phase] || null;
  };

  // Combine description message with actual messages
  const allMessages = React.useMemo(() => {
    const descriptionMessage = getPhaseDescriptionMessage(currentPhase);
    return descriptionMessage ? [descriptionMessage, ...messages] : messages;
  }, [messages, currentPhase]);

  // Auto-scroll to bottom when new messages arrive (only if user hasn't manually scrolled)
  useEffect(() => {
    if (!isUserScrolled) {
      scrollToBottom();
    } else {
      // Show scroll-to-bottom button if user has scrolled up and there are new messages
      checkScrollToBottomVisibility();
    }
  }, [messages, isUserScrolled]);

  // Smooth scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ 
      behavior: "smooth",
      block: "end"
    });
    setIsUserScrolled(false);
    setShowScrollToBottom(false);
  };

  // Check if user has manually scrolled away from bottom
  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
    
    setIsUserScrolled(!isAtBottom);
    setShowScrollToBottom(!isAtBottom && messages.length > 0);
  };

  // Check if scroll-to-bottom button should be visible
  const checkScrollToBottomVisibility = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
    setShowScrollToBottom(!isAtBottom && messages.length > 0);
  };

  // Get phase display name
  const getPhaseDisplayName = (phase) => {
    const phaseNames = {
      lobby: "Lobby Chat",
      review: "Review Discussion", 
      results: "Round Results"
    };
    return phaseNames[phase] || "Chat";
  };

  // Get phase description
  const getPhaseDescription = (phase) => {
    const descriptions = {
      lobby: "Coordinate with your team before the game starts",
      review: "Discuss answers and vote on their validity",
      results: "Celebrate the results and react to special effects"
    };
    return descriptions[phase] || "";
  };

  return (
    <div className={`flex flex-col h-full bg-white ${className}`}>
      {/* Messages container - full height with proper scrolling */}
      <div 
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-3"
      >
        {allMessages.length === 0 || (allMessages.length === 1 && allMessages[0].id?.startsWith('phase-desc')) ? (
          // Empty state (no real messages, only description)
          <div className="flex flex-col items-center justify-center h-full text-center min-h-[150px]">
            <div className="text-4xl mb-3">💬</div>
            <h4 className="text-base font-medium text-gray-700 mb-2">
              Start the conversation!
            </h4>
            <p className="text-sm text-gray-500 max-w-xs leading-relaxed">
              {currentPhase === "lobby" 
                ? "Be the first to send a message while waiting for the game!"
                : currentPhase === "review"
                ? "Share your thoughts about the round"
                : "Chat about the game results!"
              }
            </p>
          </div>
        ) : (
          // Render all messages (description + real messages)
          <div className="space-y-2 py-2">
            {allMessages.map((message, index) => (
              <Message
                key={message.id || `${message.userId}-${index}`}
                message={message}
                currentUserId={currentUserId}
              />
            ))}
            
            {/* Invisible element to scroll to */}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Scroll to bottom button */}
      {/* {showScrollToBottom && (
        <div className="hidden absolute bottom-20 right-4">
          <button
            onClick={scrollToBottom}
            className="bg-red-600 text-white p-2 rounded-full shadow-lg hover:bg-red-700 transition-all transform hover:scale-105"
            title="Scroll to bottom"
          >
            <svg 
              className="w-4 h-4" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M19 14l-7 7m0 0l-7-7m7 7V3" 
              />
            </svg>
          </button>
        </div>
      )} */}

      {/* Debug info (development only) */}
      {/* {process.env.NODE_ENV === 'development' && (
        <div className="border-t border-gray-100 px-3 py-2 bg-gray-50 text-xs text-gray-500 flex-shrink-0">
          <div className="flex justify-between items-center">
            <span>Phase: {currentPhase}</span>
            <span>Messages: {allMessages.length}</span>
            <span>Scrolled: {isUserScrolled ? 'Yes' : 'No'}</span>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default ChatWindow;